import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';

import { ReplaySubject } from 'rxjs';
import { SearchableOption } from '../../../../model/general.model';

@Component({
  selector: 'app-searchable-select',
  templateUrl: './searchable-select.component.html',
  styleUrls: ['./searchable-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SearchableSelectComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchableSelectComponent
  implements ControlValueAccessor, OnChanges
{
  @Input() options: SearchableOption<any>[] = [];
  @Input() label = 'Wybierz';
  @Input() placeholder = 'Wybierz opcję';
  @Input() searchPlaceholder = 'Szukaj';
  @Input() noEntriesFoundLabel = 'Brak wyników';
  @Input() multiSelect = true;
  @Input() useAdvancedValue = false;

  protected readonly searchControl = new FormControl<string>('', { nonNullable: true });
  protected readonly selectControl = new FormControl<number | null>(null);
  protected readonly filteredOptions$ = new ReplaySubject<SearchableOption<any>[]>(1);

  private onChange: (value: number | null) => void = () => {};
  private onTouched: () => void = () => {};
  private isDisabled = false;

  constructor() {
    this.filteredOptions$.next(this.options);

    this.searchControl.valueChanges.subscribe((searchValue) => {
      this.filterOptions(searchValue);
    });

    this.selectControl.valueChanges.subscribe((value) => {
      this.onChange(value);
      this.onTouched();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['options']) {
      this.filterOptions(this.searchControl.value);
    }
  }

  writeValue(value: number | null): void {
    this.selectControl.setValue(value, { emitEvent: false });
  }

  registerOnChange(fn: (value: number | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;

    if (isDisabled) {
      this.selectControl.disable({ emitEvent: false });
      this.searchControl.disable({ emitEvent: false });
    } else {
      this.selectControl.enable({ emitEvent: false });
      this.searchControl.enable({ emitEvent: false });
    }
  }

  protected handleOpenedChange(isOpened: boolean): void {
    if (!isOpened) {
      this.onTouched();
      this.searchControl.setValue('', { emitEvent: true });
    }
  }

  private filterOptions(searchValue: string): void {
    const phrase = (searchValue || '').toLowerCase().trim();

    if (!phrase) {
      this.filteredOptions$.next([...this.options]);
      return;
    }

    const filtered = this.options.filter((option) =>
      option.name.toLowerCase().includes(phrase)
    );

    this.filteredOptions$.next(filtered);
  }
}
